/* Unified Inbox - right pane: who this is, what we have with them, what the market says, what happened lately. */
import React from "react";
import { Link } from "react-router-dom";
import { Building2, Mail, Phone, ExternalLink, Target, Radar, Activity, Globe2, BadgeCheck, Sparkles, ArrowUpRight, Languages, Hash } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtDate, relTime } from "@/lib/format";
import { CompanyLogo, StageBadge, Money, UserAvatar, Skeleton, EmptyState } from "@/components/common";
import { ConfidenceChip, TimingChip, ValueRange } from "@/components/intel/primitives";
import { Button } from "@/components/ui/button";
import { LANG, channelMeta } from "./meta";

function Block({ icon: Icon, title, right, children, testId }) {
  return (
    <section className="px-5 py-4" data-testid={testId}>
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <h3 className="eyebrow inline-flex items-center gap-1.5 text-[10px]"><Icon className="h-3 w-3" /> {title}</h3>
        {right}
      </div>
      {children}
    </section>
  );
}

export function ContextPanel({ data, users, onCreateOpportunity, busy, className }) {
  const thread = data?.thread;
  const ctx = data?.context;
  if (!thread || !ctx) {
    return (
      <aside className={cn("h-full min-h-0 overflow-y-auto scrollbar-thin", className)} data-testid="inbox-context-pane">
        <div className="space-y-4 p-5"><Skeleton className="h-16 w-full rounded-xl" /><Skeleton className="h-24 w-full rounded-xl" /><Skeleton className="h-24 w-full rounded-xl" /></div>
      </aside>
    );
  }
  const acc = ctx.account;
  const company = thread.company || {};
  const contact = ctx.contact || thread.contact;
  const owner = acc ? (users || []).find((u) => u.id === acc.owner_id) : null;
  const opp = ctx.opportunity;
  const others = (ctx.open_opportunities || []).filter((o) => o.id !== opp?.id);
  const intel = ctx.intel;
  const isCustomer = Boolean(acc) || intel?.company?.existing_customer;
  const internal = thread.ai?.intent === "internal";

  return (
    <aside className={cn("h-full min-h-0 divide-y divide-black/[0.06] overflow-y-auto scrollbar-thin", className)} data-testid="inbox-context-pane" aria-label="Context">
      {/* company */}
      <section className="px-5 pb-4 pt-5" data-testid="inbox-ctx-company">
        <div className="flex items-start gap-3">
          <CompanyLogo name={company.name} domain={company.website} size="lg" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[15px] font-semibold leading-tight">{company.name}</div>
            <div className="mt-0.5 truncate text-[12.5px] text-muted-foreground">{[company.segment, company.city, company.country].filter(Boolean).join(" · ")}</div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {internal ? <span className="rounded-md border border-black/[0.08] bg-white px-1.5 py-[2px] text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Internal</span> : isCustomer ? <span className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--status-ok)/0.3)] bg-[hsl(var(--status-ok)/0.08)] px-1.5 py-[2px] text-[10px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--status-ok))]"><BadgeCheck className="h-3 w-3" /> Customer</span> : company.segment === "Supplier" || company.segment === "Partner" || company.segment === "Tender portal" ? <span className="rounded-md border border-black/[0.08] bg-white px-1.5 py-[2px] text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{company.segment}</span> : <span className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--primary)/0.3)] bg-[hsl(var(--primary)/0.08)] px-1.5 py-[2px] text-[10px] font-semibold uppercase tracking-[0.08em] text-[hsl(10_70%_42%)]"><Sparkles className="h-3 w-3" /> New business</span>}
              {company.website ? <a href={`https://${company.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11.5px] text-muted-foreground hover:text-foreground" data-testid="inbox-ctx-website"><Globe2 className="h-3 w-3" /> {company.website}</a> : null}
            </div>
          </div>
        </div>
        {acc ? (
          <div className="mt-3 flex items-center justify-between gap-2 rounded-xl bg-[hsl(var(--muted)/0.6)] px-3 py-2">
            <div className="flex items-center gap-2 text-[12px]">
              {owner ? <UserAvatar name={owner.name} initials={owner.initials} color={owner.color} src={owner.avatar_url} size="xs" /> : null}
              <span className="text-muted-foreground">Owner</span> <span className="font-medium">{owner?.name || "—"}</span>
              {ctx.won_count ? <span className="text-muted-foreground">· {ctx.won_count} won</span> : null}
            </div>
            <Button asChild size="sm" variant="ghost" className="h-7 gap-1 px-2 text-[12px]"><Link to={`/customers/${acc.id}`} data-testid="inbox-ctx-open-account">Account <ArrowUpRight className="h-3 w-3" /></Link></Button>
          </div>
        ) : null}
      </section>

      {/* contact */}
      {contact && contact.kind !== "system" && !internal ? (
        <Block icon={Building2} title="Contact" testId="inbox-ctx-contact">
          <div className="text-[13.5px] font-semibold">{contact.name} {contact.is_decision_maker ? <span className="ml-1 rounded-md bg-[hsl(var(--status-ok)/0.1)] px-1.5 py-[1px] text-[10px] font-semibold text-[hsl(var(--status-ok))]">Decision maker</span> : null}</div>
          <div className="text-[12.5px] text-muted-foreground">{contact.role}</div>
          <div className="mt-2 space-y-1 text-[12.5px]">
            {contact.email ? <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-foreground/85 hover:text-foreground" data-testid="inbox-ctx-contact-email"><Mail className="h-3.5 w-3.5 text-muted-foreground" /> <span className="truncate">{contact.email}</span></a> : null}
            {contact.phone ? <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-foreground/85 hover:text-foreground" data-testid="inbox-ctx-contact-phone"><Phone className="h-3.5 w-3.5 text-muted-foreground" /> {contact.phone}</a> : null}
            {!contact.email && !contact.phone ? <div className="text-muted-foreground">No confirmed contact details – reached via {channelMeta(thread.channel).label}.</div> : null}
          </div>
        </Block>
      ) : null}

      {/* opportunity */}
      {!internal && company.segment !== "Supplier" && company.segment !== "Tender portal" && company.segment !== "Partner" && thread.ai?.intent !== "noise" ? (
        <Block icon={Target} title="Opportunity" testId="inbox-ctx-opportunity">
          {opp ? (
            <Link to={`/opportunities/${opp.id}`} className="lift block rounded-xl border border-black/[0.07] bg-white p-3" data-testid="inbox-ctx-opp-card">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0"><div className="truncate text-[13.5px] font-semibold">{opp.name}</div><div className="mt-1"><StageBadge stage={opp.stage} /></div></div>
                <Money value={opp.value} currency={opp.currency || "NOK"} compact className="shrink-0 text-[13px]" />
              </div>
              {opp.next_step ? <div className="mt-2 text-[12px] text-muted-foreground"><span className="font-medium text-foreground/70">Next · </span>{opp.next_step}</div> : null}
            </Link>
          ) : (
            <div className="rounded-xl border border-dashed border-black/[0.12] p-3 text-[12.5px] text-muted-foreground">
              No opportunity yet. {thread.ai?.intent === "supplier" ? "" : "Create one to track this inquiry in the pipeline."}
              {thread.ai?.intent !== "supplier" ? <Button size="sm" variant="outline" className="mt-2 h-7 w-full rounded-lg bg-white text-[12px]" onClick={onCreateOpportunity} disabled={busy} data-testid="inbox-ctx-create-opportunity"><Target className="mr-1.5 h-3.5 w-3.5 text-primary" /> Create opportunity</Button> : null}
            </div>
          )}
          {others.length ? (
            <div className="mt-3">
              <div className="mb-1 text-[11px] font-medium text-muted-foreground">Other open opportunities</div>
              <ul className="space-y-1">
                {others.slice(0, 3).map((o) => (
                  <li key={o.id}><Link to={`/opportunities/${o.id}`} className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-[12.5px] hover:bg-black/[0.04]"><span className="truncate">{o.name}</span><Money value={o.value} currency={o.currency || "NOK"} compact className="shrink-0 text-[12px] text-muted-foreground" /></Link></li>
                ))}
              </ul>
            </div>
          ) : null}
        </Block>
      ) : null}

      {/* sales intelligence */}
      {intel?.company ? (
        <Block icon={Radar} title="Sales Intelligence" testId="inbox-ctx-intel" right={<Button asChild size="sm" variant="ghost" className="h-7 gap-1 px-2 text-[12px]"><Link to={`/intel/companies/${intel.company.id}`} data-testid="inbox-ctx-open-intel">Account 360 <ArrowUpRight className="h-3 w-3" /></Link></Button>}>
          {intel.project ? (
            <div className="rounded-xl border border-black/[0.07] bg-white p-3">
              <div className="text-[13px] font-semibold leading-snug">{intel.project.project_name}</div>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5"><TimingChip timing={intel.project.timing} phase={intel.project.estimated_phase} /><ConfidenceChip level={intel.project.confidence} short /></div>
              <div className="mt-2 text-[12px] text-muted-foreground">Est. <ValueRange label={`NOK ${Math.round(intel.project.estimated_value_low / 1e6 * 10) / 10}M – ${Math.round(intel.project.estimated_value_high / 1e6 * 10) / 10}M`} /> · estimate, not a quote</div>
            </div>
          ) : null}
          {intel.signals?.length ? (
            <ul className="mt-2.5 space-y-2">
              {intel.signals.slice(0, 3).map((s) => (
                <li key={s.id} className="text-[12.5px] leading-snug">
                  <div className="flex items-start gap-2"><span className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(210_60%_48%)]" /><div className="min-w-0"><div className="font-medium text-foreground/90">{s.title}</div><div className="text-[11.5px] text-muted-foreground">{s.source} · {fmtDate(s.detected_date)}</div></div></div>
                </li>
              ))}
            </ul>
          ) : null}
        </Block>
      ) : null}

      {/* activity */}
      {ctx.activities?.length ? (
        <Block icon={Activity} title="Recent activity" testId="inbox-ctx-activity">
          <ul className="space-y-2">
            {ctx.activities.slice(0, 5).map((a) => (
              <li key={a.id} className="flex items-start gap-2 text-[12.5px] leading-snug"><span className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/40" /><div className="min-w-0"><div className="truncate text-foreground/90">{a.title}</div><div className="text-[11px] text-muted-foreground">{relTime(a.date)}</div></div></li>
            ))}
          </ul>
        </Block>
      ) : null}

      {/* thread facts */}
      <Block icon={Hash} title="Thread" testId="inbox-ctx-thread">
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-[12.5px]">
          <dt className="text-muted-foreground">Channel</dt><dd className="font-medium">{channelMeta(thread.channel).label}</dd>
          <dt className="text-muted-foreground">Language</dt><dd className="inline-flex items-center gap-1 font-medium"><Languages className="h-3 w-3 text-muted-foreground" /> {LANG[thread.language] || "English"}</dd>
          <dt className="text-muted-foreground">First</dt><dd className="font-medium">{fmtDate(thread.first_message_at)}</dd>
          <dt className="text-muted-foreground">Last</dt><dd className="font-medium">{relTime(thread.last_message_at)}</dd>
          <dt className="text-muted-foreground">Status</dt><dd className="font-medium capitalize">{thread.status}</dd>
        </dl>
      </Block>
      {!acc && !contact && !intel ? <EmptyState title="No context" description="Nothing linked to this thread yet." className="m-5" /> : null}
    </aside>
  );
}
